import * as analytics from 'analytics/Analytics';
import {bulkUnarchiveProcessManagement} from 'archive/ArchiveActions';
import isArchived from 'archive/isArchived';
import {assert} from 'Asserts';
import * as contextmenu from 'components/ContextMenu';
import {WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {Section as NavSection, SECTIONS} from 'constants/NavConstants';
import {ProcessClientOrigin, ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {getDefaultSearchSearchParams} from 'nav/sidepanel/listing/SearchSearchParams';
import * as SidepanelReduxActions from 'nav/sidepanel/SidepanelReduxActions';
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import preferences from 'Preferences';
import {openArchiveModalForProcessItems} from 'processes/archive/archiveUtil';
import {getStore} from 'redux-stores/Store';
import {FilterKey, keyToEmptyFilter, Filter} from 'search/SearchConstants';
import * as SearchUtil from 'search/SearchUtil';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import {ProcessManagementItemType} from '../ProcessConstants';
import openWorkspaceForProcessItem from './openWorkspaceForProcessItem';
import {
  SidepanelProcessItem,
  sidepanelProcessItemIsProcessTask,
  sidepanelProcessItemIsProcess,
  sidepanelProcessItemIsProcessOutput,
} from './ProcessManagementSidepanelConstants';

function getRootProcessId(processItem: SidepanelProcessItem): string | null {
  if (sidepanelProcessItemIsProcess(processItem)) {
    if (processItem.processTaskSchema.executionType === ProcessExecutionType.FLOWCHART) {
      return processItem.id;
    } else if (processItem.rootProcess != null && !isInaccessible(processItem.rootProcess)) {
      return processItem.rootProcess.id;
    }
  }
  if (sidepanelProcessItemIsProcessTask(processItem)) {
    if (processItem.schema.executionType === ProcessExecutionType.FLOWCHART) {
      return processItem.process.id;
    } else if (processItem.process.rootProcess != null && !isInaccessible(processItem.process.rootProcess)) {
      return processItem.process.rootProcess.id;
    }
  }
  return null;
}

function getSearchTypeFromProcessItem(processItem: SidepanelProcessItem): ModelTypeConstants {
  if (sidepanelProcessItemIsProcess(processItem)) {
    return ModelTypeConstants.PROCESS;
  } else if (sidepanelProcessItemIsProcessTask(processItem)) {
    return ModelTypeConstants.PROCESS_TASK;
  } else if (sidepanelProcessItemIsProcessOutput(processItem)) {
    return ModelTypeConstants.PROCESS_OUTPUT;
  }
  assert(false, 'Unexpected listing type encountered');
}

function openSearchWithFiltersFrom(
  filterNameForAnalytics: string,
  processItem: SidepanelProcessItem,
  searchFilters: Array<Filter>
): void {
  analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.CLICKED_DASHBOARD_CONTEXT_MENU_FILTER, {
    filter_name: filterNameForAnalytics,
  });

  getStore().dispatch(
    SidepanelReduxActions.openListing(
      getCurrentNavSection(),
      SearchUtil.mergeFiltersToSearchParams(
        getDefaultSearchSearchParams(),
        maybeAddClientOrigin(processItem, searchFilters)
      )
    )
  );
}

function initializeNewFilters(searchType: ModelTypeConstants): Array<Filter> {
  return [
    {
      ...keyToEmptyFilter(FilterKey.TYPES),
      value: [searchType],
    },
  ];
}

function getCurrentNavSection(): NavSection {
  const currSection = SidepanelStore.getSection();
  if (currSection != null && SECTIONS.hasOwnProperty(currSection)) {
    return currSection as NavSection;
  } else {
    return NavSection.SEARCH;
  }
}

function getClientOriginForItem(processItem: SidepanelProcessItem): ProcessClientOrigin {
  if (sidepanelProcessItemIsProcess(processItem)) {
    return processItem.processTaskSchema.clientOrigin;
  } else if (sidepanelProcessItemIsProcessTask(processItem)) {
    return processItem.schema.clientOrigin;
  } else if (sidepanelProcessItemIsProcessOutput(processItem)) {
    return processItem.task.schema.clientOrigin;
  }
  assert(false, 'Unexpected listing type encountered');
}

function maybeAddClientOrigin(processItem: SidepanelProcessItem, filters: Array<Filter>): Array<Filter> {
  const currentNavSection = getCurrentNavSection();
  if (currentNavSection !== NavSection.PROCESSES) {
    return filters;
  }

  const clientOrigin = getClientOriginForItem(processItem);
  if (clientOrigin != null) {
    filters.push({
      ...keyToEmptyFilter(FilterKey.PROCESS_CLIENT_ORIGIN),
      value: clientOrigin,
    });
  }
  return filters;
}

export function getContextMenuOptions(
  selectedType: ProcessManagementItemType,
  processItem: SidepanelProcessItem
): Array<contextmenu.ContextMenuOption> {
  return [
    {
      name: 'Open',
      click: () => {
        openWorkspaceForProcessItem(processItem);
      },
    },
    {
      name: 'Archive',
      click: () => {
        openArchiveModalForProcessItems({
          itemType: selectedType,
          items: [processItem],
        });
      },
      disabled: () => !processItem.permissions.archivable,
      hide: () => isArchived(processItem),
    },
    {
      name: 'Unarchive',
      click: () => {
        bulkUnarchiveProcessManagement(selectedType, [processItem.id]);
      },
      disabled: () => !processItem.permissions.unarchivable,
      hide: () => !isArchived(processItem),
    },
    {
      name: 'Copy API ID',
      click: () => {
        copyToClipboard(processItem.id, {showToastOnSuccess: true});
      },
      hide: () => !preferences.get('ENABLE_COPY_API_ID'),
    },
    '-',
    {
      name: 'Filter to...',
      hide: getClientOriginForItem(processItem) !== ProcessClientOrigin.PROCESSES,
      menu: [
        {
          name: 'Tasks in this task group',
          click: () => {
            const searchFilters = initializeNewFilters(ModelTypeConstants.PROCESS_TASK);

            searchFilters.push({
              ...keyToEmptyFilter(FilterKey.PROCESS),
              value: [processItem.id],
            });

            openSearchWithFiltersFrom('tasks_in_process', processItem, searchFilters);
          },
          hide: () => !sidepanelProcessItemIsProcess(processItem),
        },
        {
          name: 'This schema',
          click: () => {
            const searchType = getSearchTypeFromProcessItem(processItem);
            let schemaIdentifier;

            if (sidepanelProcessItemIsProcessTask(processItem)) {
              schemaIdentifier = processItem.schema.id;
            } else if (sidepanelProcessItemIsProcess(processItem)) {
              schemaIdentifier = processItem.processTaskSchema.id;
            } else if (sidepanelProcessItemIsProcessOutput(processItem)) {
              schemaIdentifier = processItem.schema.id;
            } else {
              assert(false, 'Unexpected listing type encountered');
            }

            const searchFilters = initializeNewFilters(searchType);

            searchFilters.push({
              ...keyToEmptyFilter(FilterKey.SCHEMA),
              value: [schemaIdentifier],
            });

            openSearchWithFiltersFrom('current_schema', processItem, searchFilters);
          },
        },
        {
          name: 'This flowchart',
          click: () => {
            const rootFlowchartProcessId = getRootProcessId(processItem);

            // to satisfy the typescript gods
            assert(rootFlowchartProcessId != null, 'Expected process item to have a root flowchart process');

            const searchType = getSearchTypeFromProcessItem(processItem);
            const searchFilters = initializeNewFilters(searchType);

            searchFilters.push({
              ...keyToEmptyFilter(FilterKey.ROOT_PROCESS),
              value: [rootFlowchartProcessId],
            });

            openSearchWithFiltersFrom('current_flowchart', processItem, searchFilters);
          },
          hide: () => getRootProcessId(processItem) == null,
        },
      ],
    },
  ];
}

export function open(
  e: Event,
  selectedType: ProcessManagementItemType,
  processItem: SidepanelProcessItem
): void {
  contextmenu.view.open(e, getContextMenuOptions(selectedType, processItem));
}
